import type { MapLayoutValidationIssue } from "@/lib/office/map-layout-validate";
import { cn } from "@/lib/utils";
import type { EditorItemRef } from "./map-layout-utils";

export type LayoutIssueRow = MapLayoutValidationIssue & {
  /** Publish-only soft errors (e.g. owned offices). */
  severity?: "error" | "warning";
};

type Props = {
  issues: LayoutIssueRow[];
  onSelect: (selection: EditorItemRef) => void;
  className?: string;
};

export function MapLayoutIssues({ issues, onSelect, className }: Props) {
  if (issues.length === 0) {
    return (
      <div
        className={cn(
          "rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-200",
          className,
        )}
      >
        Layout looks valid — ready to publish.
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex max-h-36 flex-col overflow-hidden rounded-md border border-amber-500/40 bg-amber-500/10",
        className,
      )}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-amber-500/20 px-3 py-1.5">
        <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">
          {issues.length} issue{issues.length === 1 ? "" : "s"} to fix before publish
        </p>
        <span className="text-[10px] text-muted-foreground">Click a row to select on the map</span>
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto">
        {issues.map((issue) => {
          const clickable = !!issue.selection;
          const selKey =
            issue.selection?.kind === "spawn"
              ? `spawn:${issue.selection.index}`
              : issue.selection
                ? `${issue.selection.kind}:${issue.selection.id}`
                : "none";
          return (
            <li key={`${issue.path}:${selKey}:${issue.message}`}>
              <button
                type="button"
                disabled={!clickable}
                onClick={() => {
                  if (issue.selection) onSelect(issue.selection);
                }}
                className={cn(
                  "flex w-full gap-2 border-b border-amber-500/10 px-3 py-1.5 text-left text-xs last:border-0",
                  clickable ? "cursor-pointer hover:bg-amber-500/20" : "cursor-default opacity-90",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full",
                    issue.severity === "warning" ? "bg-amber-400" : "bg-red-500",
                  )}
                />
                <span className="min-w-0 flex-1 text-foreground/90">{issue.message}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
